import type { ErrorComponentProps } from "@tanstack/react-router";
import { L } from "@/components/l";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, useRouter, HeadContent, Scripts } from "@tanstack/react-router";
import { Suspense, use, useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { ThemeProvider, themeInitScript } from "../lib/theme";
import { PwaInstall } from "../components/pwa-install";
import { ServiceWorkerUpdater } from "../components/sw-update";
import { Toaster } from "../components/ui/sonner";
import { useT, useLocale, HTML_LANG } from "../i18n";
import { ensureAutoDict, isAutoDictReady, setAutoLocale } from "../i18n/auto";

function NotFoundComponent() {
  const t = useT();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p
          className="font-mono text-6xl font-bold tracking-tighter text-primary"
          aria-hidden="true"
        >
          4<span className="inline-block scale-x-50">0</span>4
        </p>
        <h1 className="mt-4 text-2xl font-bold text-foreground">
          {t("notFound.title")}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t("notFound.body")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <L
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("notFound.ctaPrimary")}
          </L>
          <L
            to="/compress-gif-for-discord"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("notFound.ctaSecondary")}
          </L>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  const t = useT();
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {t("error.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("error.body")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("error.retry")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("error.home")}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "ZipGIF — Compress GIFs Online Free, No Uploads" },
      {
        name: "description",
        content:
          "Shrink GIF file size by up to 70% in seconds. ZipGIF compresses GIFs in your browser — no uploads, no watermark, no signup.",
      },
      { name: "author", content: "ZipGIF" },
      { name: "theme-color", content: "#2563EB" },
      { property: "og:title", content: "ZipGIF — Compress GIFs Online Free" },
      {
        property: "og:description",
        content: "Every GIF tool. Zero uploads. Compress GIFs privately in your browser.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ZipGIF" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "ZipGIF" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "application-name", content: "ZipGIF" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
        href: "https://fonts.gstatic.com/s/inter/v20/UcC73FwrK3iLTeHuS_nVMrMxCp50SjIa1ZL7W0Q5nw.woff2",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      // Google Analytics (G-DVZPWB48B2) is delivered through the Tag Manager
      // container injected in src/server.ts — loading gtag.js here as well
      // double-counted visits and shipped the same 173 KB script twice.
      // Preserve existing theme init script
      { children: themeInitScript },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const locale = useLocale();
  setAutoLocale(locale);
  return (
    <html lang={HTML_LANG[locale]} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Toaster richColors position="top-center" />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Holds rendering until the current locale's translation chunk is in memory.
 * English resolves instantly and downloads nothing.
 */
function LocaleGate({ children }: { children: ReactNode }) {
  const locale = useLocale();
  if (!isAutoDictReady(locale)) {
    use(ensureAutoDict(locale));
  }
  setAutoLocale(locale);
  return <>{children}</>;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Suspense fallback={null}>
          <LocaleGate>
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
          </LocaleGate>
        </Suspense>
        <PwaInstall />
        <ServiceWorkerUpdater />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
